"use client";

import Script from "next/script";
import { useCallback } from "react";

const clarityTagId = process.env.NEXT_PUBLIC_CLARITY_TAG_ID;
const isProduction = process.env.NODE_ENV === "production";

export function ClarityAnalyticsScript() {
	if (!isProduction || !clarityTagId) {
		return null;
	}

	// 拆成两段：队列桩随 HTML 同步执行，外部 tag 等页面 load 后空闲时再拉。
	// useClarityAnalytics 只检查 window.clarity 是否存在，桩一装就存在，
	// 之后的 event/upgrade 调用进 clarity.q，tag 加载后回放，事件不会丢。
	// 之前 afterInteractive 让 Clarity 的 87ms 脚本执行落在 hydration 窗口里
	// （PSI 2026-09-27，主线程耗时前四）。不用"首次交互后加载"，那会把这段
	// 执行排在用户第一次点击之后，正好落进 INP 窗口。
	return (
		<>
			<script
				id="microsoft-clarity-stub"
				// biome-ignore lint/security/noDangerouslySetInnerHtml: 静态队列桩，无用户输入
				dangerouslySetInnerHTML={{
					__html: "window.clarity=window.clarity||function(){(window.clarity.q=window.clarity.q||[]).push(arguments)};",
				}}
			/>
			<Script
				id="microsoft-clarity"
				strategy="lazyOnload"
				src={`https://www.clarity.ms/tag/${clarityTagId}`}
				crossOrigin="anonymous"
				onError={(e) => {
					console.error("Clarity script error:", e);
				}}
			/>
		</>
	);
}

// Clarity batches its uploads and exposes no flush callback, so the only way to
// keep an event alive across a navigation is to hold the caller briefly.
const CLARITY_FLUSH_MS = 300;

export function useClarityAnalytics() {
	// Clarity's event API takes the event name only — a third argument for
	// properties is silently dropped. Per-event dimensions belong in Umami; here
	// the recording itself carries the context.
	//
	// Returns a promise callers can await before a navigation that would
	// otherwise drop the batched event. Resolves immediately when Clarity is
	// absent, so non-production and blocked-script cases add no delay.
	const trackEvent = useCallback((event: string): Promise<void> => {
		if (!isProduction) {
			return Promise.resolve();
		}

		if (typeof window === "undefined" || !(window as any).clarity) {
			return Promise.resolve();
		}

		(window as any).clarity("event", event);
		return new Promise((resolve) => setTimeout(resolve, CLARITY_FLUSH_MS));
	}, []);

	// Clarity keeps at most 100k recordings per project per day and samples the
	// rest away. This flags the session as one worth keeping.
	const prioritizeRecording = useCallback((reason: string) => {
		if (!isProduction) {
			return;
		}

		if (typeof window !== "undefined" && (window as any).clarity) {
			(window as any).clarity("upgrade", reason);
		}
	}, []);

	return {
		trackEvent,
		prioritizeRecording,
	};
}
